<template>
  <div class="library__article">
    <p>
      <VisibleText>Go to </VisibleText>
      <router-link to="/library">
        <VisibleText>Library</VisibleText>
      </router-link>
    </p>

    <h1 class="library__h1">
      <VisibleText>Building Institutional Knowledge</VisibleText>
    </h1>

    <div class="library__heading-line"></div>

    <p>
      <VisibleText>2026-06-15</VisibleText>
    </p>

    <img
      class="library__image"
      style="max-height: 15rem; max-width: 95%"
      src="/assets/landing/build_institutional_knowledge.jpg"
      alt=""
    />

    <h2 class="library__h2">
      <VisibleText>
        An implementation roadmap for organizations ready to move from AI aspiration to validated
        intelligence
      </VisibleText>
    </h2>

    <p>
      <VisibleText>
        Every organization in this series started from a recognizable position: data they had
        collected but could not fully use, analytical ambition that exceeded analytical capacity,
        and a gap between what the feedback was telling them and what the organization was able to
        hear.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        What separated the ones that closed that gap from the ones that did not was not budget, or
        headcount, or access to more sophisticated technology. It was methodology. A structured
        approach to building analytical capability that starts with the right question, builds the
        right framework, validates before it generates, and closes the loop between findings and
        action.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        This article describes that approach — the ResearchAI Value Creation process — as a
        practical implementation roadmap. Not a theoretical framework. A sequence of steps that any
        public sector team can follow, regardless of where they are starting from.
      </VisibleText>
    </p>

    <h2 class="library__h2">
      <VisibleText>The Ten-Stage Value Creation Process</VisibleText>
    </h2>

    <p>
      <VisibleText>
        The process is organized as a continuous cycle of ten stages, each building on the last. The
        first time through the cycle, an organization builds its analytical foundation. Every
        subsequent cycle compounds on it — adding data, refining dictionaries, deepening
        comparisons, tracking improvement.
      </VisibleText>
    </p>

    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>Stage</th>
          <th>What Happens</th>
          <th>What It Looks Like in Practice</th>
        </tr>
      </thead>

      <tbody>
        <tr>
          <td>1</td>
          <td>Context</td>
          <td>
            Define the organizational question precisely. Not 'are clients satisfied?' but 'what is
            this service system producing, and how much of what clients experience is the cost of
            failure rather than the value of service?'
          </td>
          <td>
            A Crown Corporation's Strategic Services team reframes its CSI survey purpose from
            satisfaction measurement to service system diagnosis before a single response is read.
          </td>
        </tr>

        <tr>
          <td>2</td>
          <td>Model</td>
          <td>
            Select or build the measurement framework. Established instruments — INTSERVQUAL, CMT,
            SERVQUAL, NPS driver frameworks — provide validated starting structures that can be
            adapted to the organization's specific context.
          </td>
          <td>
            The internal services team combines INTSERVQUAL and the ICCS Common Measurements Tool
            into a nine-theme cross-unit framework applied identically across eleven service lines.
          </td>
        </tr>

        <tr>
          <td>3</td>
          <td>Data</td>
          <td>
            Audit the evidence base before analysis begins. Assess completeness, identify labelling
            artifacts, understand what the response patterns themselves reveal about the survey
            instrument.
          </td>
          <td>
            An 86% Q4 completion rate against a 58% Q5 rate — the asymmetry itself a finding about
            how internal clients experience the feedback process.
          </td>
        </tr>

        <tr>
          <td>4</td>
          <td>Dictionary</td>
          <td>
            Build the analytical engine. Validated keywords and phrases — drawn from the
            organizational data, domain theory, and service-specific language — applied consistently
            across the full dataset.
          </td>
          <td>
            A Failure Demand dictionary grounded in 904 actual yukon.ca ‘not helpful’ responses; a
            206-term dictionary built from 1,200+ Texas DMV Google Reviews. Both are
            organizationally specific. Neither is generic.
          </td>
        </tr>

        <tr>
          <td>5</td>
          <td>Validation</td>
          <td>
            Test the dictionary against real responses before running the full analysis. Coverage
            rate sense-testing, misfire audit (false positives), and gap audit (untagged responses
            revealing omissions).
          </td>
          <td>
            False positive examples — common words triggering incorrect theme matches — corrected in
            the room with the feedback team. The dictionary is owned by the organization, not the
            vendor.
          </td>
        </tr>

        <tr>
          <td>6</td>
          <td>Analysis</td>
          <td>
            Apply the validated dictionary to the full dataset. Cross-unit coverage matrices,
            co-occurrence patterns, time series, Failure Demand rates by service line or department.
          </td>
          <td>
            All eleven service lines against all nine themes simultaneously — a cross-unit
            comparison the organization had never previously been able to produce.
          </td>
        </tr>

        <tr>
          <td>7</td>
          <td>Insights</td>
          <td>
            Translate findings into organizational decisions. Each insight linked to a specific
            decision and a specific decision-owner. Distinguish system problems from people
            problems, active risk from improvement opportunity.
          </td>
          <td>
            Six structured insights developed from the cross-unit analysis, each connected to a
            tier-sorted action, a success measure, and a prediction about the next survey cycle.
          </td>
        </tr>

        <tr>
          <td>8</td>
          <td>Reporting</td>
          <td>
            Produce audience-specific outputs. Executive brief, service line briefs, cross-unit
            dashboard, Failure Demand register. GenAI-generated narrative summaries validated
            against the analytical output before inclusion.
          </td>
          <td>
            Four report types produced from a single validated analysis: one-page executive brief,
            eleven service line briefs, cross-unit dashboard, escalation register.
          </td>
        </tr>

        <tr>
          <td>9</td>
          <td>Action</td>
          <td>
            Close the loop. Three-tier action register: manager authority, director authority,
            immediate escalation. Every action linked to a predicted theme coverage shift in the
            next cycle.
          </td>
          <td>
            Eight Tier 1 actions, five Tier 2 director reviews, two Tier 3 immediate escalations —
            including a patient safety adjacency that required leadership attention within 30 days.
          </td>
        </tr>

        <tr>
          <td>10</td>
          <td>Impact</td>
          <td>
            Measure whether change occurred and feed findings back into the next Context stage. The
            cycle becomes continuous. Improvement becomes testable. The feedback loop closes and
            compounds.
          </td>
          <td>
            The 2022 baseline establishes coverage rates and Failure Demand rates for all eleven
            service lines. Every subsequent cycle is a comparison. Improvement is no longer asserted
            — it is measured.
          </td>
        </tr>
      </tbody>
    </table>

    <p>
      <VisibleText>
        "Implementation is not software installation. It is knowledge capture — building the
        organizational capability to ask better questions of the evidence you already hold."
      </VisibleText>
    </p>

    <h2 class="library__h2">
      <VisibleText>What the Case Studies Show About How to Start</VisibleText>
    </h2>

    <p>
      <VisibleText>
        Across the five case studies in this series, three practical principles emerge for
        organizations beginning the implementation journey.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        <strong>Start with the question, not the data.</strong> The most common implementation
        failure is loading data into a platform before defining what the organization needs to know.
        The SNB internal services engagement reframed its analytical purpose before a single
        response was read. The Yukon e-Services team identified five non-negotiable requirements
        before selecting a platform. The airport CX teams that achieved the most sophisticated
        intelligence built their measurement taxonomy before deploying any analytical tool. The
        question shapes everything that follows.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        <strong>Build the dictionary from your own data.</strong> Generic dictionaries produce
        generic findings. Every high-performing dictionary in this series — the Failure Demand
        framework for yukon.ca, the internal services model for the Crown Corporation, the 206-term
        Failure Demand dictionary for the DMV case — was grounded in the organization’s actual
        feedback language. Keywords and phrases that no off-the-shelf tool would recognize;
        service-specific terms, process identifiers, organizational shorthand. This organizational
        specificity is what makes the output defensible and the findings actionable.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        <strong>Design the capability transfer from the start.</strong> The most significant outcome
        of the SNB engagement was not any individual finding. It was what the two-person Strategic
        Services team could do independently at the end of six months. The ResearchAI methodology is
        designed for capability transfer — the organization owns the dictionary, understands the
        validation logic, and runs subsequent cycles without external dependency. This is the
        difference between a consulting engagement and an organizational capability.
      </VisibleText>
    </p>

    <h2 class="library__h2">
      <VisibleText>The Phased Implementation Path</VisibleText>
    </h2>

    <p>
      <VisibleText>
        Most organizations do not need to implement all ten stages simultaneously. A phased approach
        limits financial exposure, builds team confidence, and produces early results that justify
        expansion.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        <strong>Phase 1 — Single use case (weeks 1–6).</strong> Choose one dataset, one question,
        one dictionary. Apply stages 1 through 9. Produce a report. Identify the three most
        actionable findings. This phase proves the methodology works in your organizational context
        and establishes the first baseline.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        <strong>Phase 2 — Expand and compare (months 2–6).</strong> Refine the dictionary based on
        Phase 1 validation. Add a second data source or a second service line. Run stages 6 through
        9 comparatively. The cross-unit capability — seeing patterns that span organizational
        boundaries — becomes available at this stage.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        <strong>Phase 3 — Continuous intelligence (month 6+).</strong> API integration enables
        real-time or scheduled data ingestion. Time series analysis tracks whether the actions taken
        in Phase 1 and 2 produced measurable change. The tenth stage — Impact — connects each cycle
        to the next. The system becomes self-improving.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        Meaningful results are available in weeks. Enterprise-scale capability is built over months.
        The investment compounds with every cycle. The cost of not starting is three years of data
        sitting unanalyzed.
      </VisibleText>
    </p>

    <table>
      <thead>
        <tr>
          Next in the series:
          <router-link to="/library/feature/trusted_ai_series_public_sector_strategy"
            >Article 6 — Trusted AI: Public Sector Strategy</router-link
          >
        </tr>
      </thead>
    </table>

    <h3>
      <VisibleText>About ResearchAI</VisibleText>
    </h3>

    <p>
      <VisibleText>
        ResearchAI (research-ai.io) is a Canadian text analytics platform developed by PigeonLine
        Ltd. It combines traditional data science with strategically applied GenAI to transform
        unstructured feedback into validated organizational intelligence. Used by federal,
        provincial, and territorial governments across Canada, and by public sector partners in the
        UK and internationally.
      </VisibleText>
    </p>

    <div class="library__textbox" style="padding: 0.5em">
      <div class="library__textbox__col">
        <img
          class="library__image library__image--nonresponsive"
          style="max-width: 4rem"
          src="/assets/landing/analyze_icon.png"
          alt=""
        />
      </div>
      <div class="library__textbox__col">
        <ul>
          <li>
            <VisibleText>
              If you are an existing user,
              <router-link to="/login">login to ResearchAI</router-link>
              and try something new. Or reach out to us at:
              <a href="mailto:hello@researchai.io">hello@researchai.io</a>
            </VisibleText>
          </li>
          <li>
            <VisibleText>
              If you are finding us for the first time,
              <router-link to="/register">register for a free ResearchAI account.</router-link>
              We will be in touch to set you up for success.
            </VisibleText>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { useHead } from '@unhead/vue';
import { computed, reactive } from 'vue';

export default {
  name: 'BuildingInstitutionalKnowledge',

  setup() {
    const siteData = reactive({
      title: 'The Trusted AI Series · Building Institutional Knowledge',
      description:
        'An implementation roadmap for organizations ready to move from AI aspiration to validated intelligence.',
    });

    useHead({
      title: computed(() => siteData.title),
      meta: [
        {
          name: 'description',
          content: computed(() => siteData.description),
        },
      ],
    });

    return { siteData };
  },

  methods: {
    goto(refName) {
      this.$refs[refName].scrollIntoView();
    },
  },
};
</script>
